import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import { getApiUrl } from '../utils/apiConfig';
import { studentHasCourseName } from '../utils/studentCourses';
import { hasSuperUserAccess } from '../utils/sessionUser';

const CHI_GUNG_DISPLAY = 'Chi Gung';

/** Canonical order for the six Chi Gung form entries. */
const FORM_NAMES_ORDER = [
  'Form 1',
  'Form 2',
  'Form 3',
  'Form 3 (earth)',
  'Form 4',
  'Form 4 (earth)',
];

function sortFormEntries(entries) {
  const rank = (name) => {
    const i = FORM_NAMES_ORDER.findIndex(
      (n) => n.localeCompare(String(name ?? ''), undefined, { sensitivity: 'base' }) === 0
    );
    return i === -1 ? 999 : i;
  };
  return [...(entries || [])].sort(
    (a, b) => rank(a.name) - rank(b.name) || (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
  );
}

export default function ChiGungHandbookPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const user = route?.params?.user ?? null;
  const courseId = route?.params?.courseId ?? null;
  const courseName = route?.params?.courseName ?? CHI_GUNG_DISPLAY;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [synopsis, setSynopsis] = useState('');
  const [entries, setEntries] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (courseId == null || user?.id == null) {
        setLoading(false);
        setError(true);
        return;
      }
      if (
        !hasSuperUserAccess(user) &&
        !studentHasCourseName(user, courseName)
      ) {
        setLoading(false);
        setError(true);
        return;
      }
      setLoading(true);
      setError(false);
      try {
        const r = await fetch(
          `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}/handbook?viewer_id=${encodeURIComponent(String(user.id))}`
        );
        if (!r.ok) {
          if (!cancelled) setError(true);
          return;
        }
        const data = await r.json();
        if (cancelled) return;
        setSynopsis(String(data?.handbook?.synopsis ?? '').trim());
        setEntries(sortFormEntries(data?.entries ?? []));
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [courseId, user, courseName]);

  const goBack = useCallback(() => {
    if (user && courseId != null) {
      navigation.navigate('MyCourseDetail', {
        user,
        courseId,
        courseName: courseName || CHI_GUNG_DISPLAY,
      });
    } else {
      navigation.goBack();
    }
  }, [navigation, user, courseId, courseName]);

  const openForm = useCallback(
    (entry) => {
      if (!entry?.id || !entry?.route_slug) return;
      navigation.navigate('ChiGungHandbookForm', {
        user,
        courseId,
        courseName: courseName || CHI_GUNG_DISPLAY,
        entryId: entry.id,
        entrySlug: entry.route_slug,
        formTitle: entry.name,
      });
    },
    [navigation, user, courseId, courseName]
  );

  const styles = useMemo(() => getStyles(theme), [theme]);

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'chi-gung-handbook' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={styles.breadcrumbContainer}
          onPress={goBack}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Back to course"
        >
          <Text style={styles.breadcrumbBack} numberOfLines={1}>
            ‹ {courseName || CHI_GUNG_DISPLAY}
          </Text>
        </TouchableOpacity>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={user} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        {...(Platform.OS === 'web' ? { dataSet: { chiGungHandbookScroll: 'true' } } : {})}
      >
        <Text style={[styles.pageTitle, { color: theme.colors.heading }]}>Chi Gung handbook</Text>

        {loading ? (
          <View style={styles.centerBlock}>
            <ActivityIndicator color={theme.colors.heading} />
          </View>
        ) : error ? (
          <Text style={[styles.body, { color: theme.colors.text }]}>
            Handbook is not available yet. Ask an instructor to publish the Chi Gung handbook for this
            course.
          </Text>
        ) : (
          <>
            <Text style={[styles.synopsisLabel, { color: theme.colors.text, opacity: 0.65 }]}>
              Synopsis
            </Text>
            <Text style={[styles.synopsisBody, { color: theme.colors.text }]}>
              {synopsis || 'No synopsis has been added yet.'}
            </Text>

            <Text style={[styles.formsHeading, { color: theme.colors.heading }]}>Forms</Text>
            <View style={styles.bulletList}>
              {entries.map((e) => (
                <TouchableOpacity
                  key={e.id}
                  style={styles.bulletRow}
                  onPress={() => openForm(e)}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={`Open ${e.name}`}
                >
                  <Text style={[styles.bullet, { color: theme.colors.heading }]}>•</Text>
                  <Text style={[styles.bulletText, { color: theme.colors.heading }]}>{e.name}</Text>
                  <Text style={[styles.bulletChevron, { color: theme.colors.heading }]}>›</Text>
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}
      </ScrollView>
      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: { marginRight: 15 },
    breadcrumbContainer: { flex: 1, minWidth: 0 },
    breadcrumbBack: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {},
    content: { flex: 1 },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 40,
    },
    centerBlock: {
      paddingVertical: 32,
      alignItems: 'center',
    },
    pageTitle: {
      fontSize: 24,
      fontWeight: '700',
      marginBottom: 20,
    },
    synopsisLabel: {
      fontSize: 13,
      fontWeight: '600',
      marginBottom: 6,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    synopsisBody: {
      fontSize: 15,
      lineHeight: 22,
      marginBottom: 28,
    },
    formsHeading: {
      fontSize: 18,
      fontWeight: '700',
      marginBottom: 12,
    },
    bulletList: {
      alignSelf: 'stretch',
    },
    bulletRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.isDarkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0',
    },
    bullet: {
      fontSize: 18,
      marginRight: 10,
      fontWeight: '700',
    },
    bulletText: {
      flex: 1,
      fontSize: 16,
      fontWeight: '600',
    },
    bulletChevron: {
      fontSize: 22,
      fontWeight: '300',
    },
    body: {
      fontSize: 15,
      lineHeight: 22,
    },
  });
}
